import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { createChoice, createQuestion, deleteChoice, deleteQuestion, updateChoice, UpdateQuestion } from "../store/assessmentsSlice";
import { toast } from "react-toastify";
import CustomInput from "../components/CustomInput";
import CustomButton from "../components/CustomButton";
import { Col, Row } from "react-bootstrap";
import { Button, Tooltip } from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";

const CreateUpdateQuestions = ({ data = [], assessment_id, onSubmitChoice }) => {
    const [questionsData, setQuestionsData] = useState([]);
    const dispatch = useDispatch();

    useEffect(() => {
        if (data?.length > 0) {
            setQuestionsData(data);
        } else { setQuestionsData([]) }
    }, [data, data?.length]);

    const formik = useFormik({
        initialValues: {
            questions: questionsData.length > 0
                ? questionsData.map((question) => ({
                    id: question.id || null,
                    text: question.text || "",
                    assessment: assessment_id,
                    choices: question.choices.map((choice, index) => ({
                        id: choice.id || null,
                        label: String.fromCharCode(97 + index), // Labels: a, b, c, ...
                        text: choice.text || "",
                        is_correct: choice.is_correct || false,
                        is_submitting: false
                    })),
                }))
                : [
                    {
                        id: null,
                        text: "",
                        assessment: assessment_id,
                        choices: [
                            { id: null, label: "a", text: "", is_correct: false, is_submitting: false },
                            { id: null, label: "b", text: "", is_correct: false, is_submitting: false },
                        ],
                    },
                ],
        },
        enableReinitialize: true,
        validationSchema: Yup.object().shape({
            questions: Yup.array().of(
                Yup.object().shape({
                    text: Yup.string().required("Question text is required"),
                    choices: Yup.array()
                        .of(
                            Yup.object().shape({
                                label: Yup.string(),
                                text: Yup.string().required("Choice text is required"),
                                is_correct: Yup.boolean(),
                            })
                        )
                })
            ),
        }),
        onSubmit: () => { },
    });

    // Handle adding a new question
    const handleAddQuestion = () => {
        formik.setFieldValue("questions", [
            ...formik.values.questions,
            {
                id: null,
                text: "",
                assessment: assessment_id,
                choices: [
                    { id: null, label: "a", text: "", is_correct: false, is_submitting: false },
                    { id: null, label: "b", text: "", is_correct: false, is_submitting: false },
                ],
            },
        ]);
    };

    // Handle adding a new choice
    const handleAddChoice = (questionIndex) => {
        const updatedQuestions = [...formik.values.questions];
        const currentChoices = updatedQuestions[questionIndex].choices;

        const nextChoiceLabel = String.fromCharCode(97 + currentChoices.length); // Next label (a, b, c, ...)

        updatedQuestions[questionIndex].choices.push({
            id: null,
            label: nextChoiceLabel,
            text: "",
            is_correct: false,
            is_submitting: false
        });

        formik.setFieldValue("questions", updatedQuestions);
        setQuestionsData(updatedQuestions);
    };

    // Handle deleting a choice
    const handleDeleteChoice = async (questionIndex, choiceIndex) => {
        const question = formik.values.questions[questionIndex];
        const choice = question.choices[choiceIndex];

        // If there's no choice id, delete only on the front-end
        if (!choice.id) {
            // Delete choice from the front-end only
            const updatedQuestions = [...formik.values.questions];
            updatedQuestions[questionIndex].choices.splice(choiceIndex, 1); // Remove choice at the index

            // Update labels after deletion
            updatedQuestions[questionIndex].choices = updatedQuestions[questionIndex].choices.map((choice, index) => ({
                ...choice,
                label: String.fromCharCode(97 + index), // Update labels dynamically
            }));

            formik.setFieldValue("questions", updatedQuestions);
            setQuestionsData(updatedQuestions); // Update the state
            return; // Exit early as no API call is necessary
        }

        // If the choice has an ID, call the API to delete it from the back-end

        try {
            formik.setFieldValue(
                `questions[${questionIndex}].choices[${choiceIndex}].is_deleting`,
                true
            );
            
            // Make the API call to delete the choice
            await dispatch(deleteChoice({ choice_id: choice.id })); // Replace with your API call

            // If the choice was deleted successfully, remove it from the questions array
            const updatedQuestions = [...formik.values.questions];
            updatedQuestions[questionIndex].choices.splice(choiceIndex, 1); // Remove choice at the index

            // Update labels
            updatedQuestions[questionIndex].choices = updatedQuestions[questionIndex].choices.map((choice, index) => ({
                ...choice,
                label: String.fromCharCode(97 + index), // Update labels dynamically
            }));

            formik.setFieldValue(
                `questions[${questionIndex}].choices[${choiceIndex}].is_deleting`,
                false
            );
            // Update Formik values
            formik.setFieldValue("questions", updatedQuestions);
            setQuestionsData(updatedQuestions);

            // Alert user about success
            toast.success("Choice deleted successfully!");
        } catch (error) {
            toast.error("Failed to delete choice.");
        }
    };

    const handleSave = async (questionIndex) => {
        const question = formik.values.questions[questionIndex];
        let isQuestionSaved = false;
        let areChoicesSaved = true;

        try {
            // Step 1: Save or update the question
            formik.setFieldValue(`questions[${questionIndex}].is_submitting`, true);
            let savedQuestion;

            if (question.id) {
                // Update the existing question
                savedQuestion = await dispatch(UpdateQuestion({ ...question, question_id: question.id })).unwrap();
                isQuestionSaved = true;
            } else {
                // Create a new question
                savedQuestion = await dispatch(createQuestion(question)).unwrap();
                isQuestionSaved = true;
            }

            // After saving or updating the question, update the question ID
            formik.setFieldValue(`questions[${questionIndex}].id`, savedQuestion?.data?.id);

            // Step 2: Now handle options (choices) creation or update
            for (let choiceIndex = 0; choiceIndex < question.choices.length; choiceIndex++) {
                const choice = question.choices[choiceIndex];

                // Prepare choice data
                const formData = new FormData();
                formData.append('text', choice.text);
                formData.append('is_correct', choice.is_correct);
                formData.append('question', savedQuestion?.data?.id); // Use the saved question's ID

                try {
                    if (choice.id) {
                        // Update the existing choice (pass choice.id for update)
                        formData.append('choice_id', choice.id);  // Pass the choice_id for update
                        await dispatch(updateChoice(formData)).unwrap();
                    } else {
                        // Create the choice (no choice.id)
                        await dispatch(createChoice(formData)).unwrap();
                    }
                } catch (choiceError) {
                    areChoicesSaved = false; // Mark that at least one choice failed to save
                    console.error("Failed to save choice:", choiceError);
                }

                // After saving or updating the choice, update the choice ID (if new choice was created)
                formik.setFieldValue(
                    `questions[${questionIndex}].choices[${choiceIndex}].id`,
                    choice.id || savedQuestion?.data?.id  // Update the choice ID (in case of new choice)
                );
            }

            // Step 3: Show a single success message if both the question and choices were saved/updated successfully
            if (isQuestionSaved && areChoicesSaved) {
                toast.success("Question and choices saved/updated successfully!");
            }

            // Reset the submitting flag after all operations are complete
            formik.setFieldValue(`questions[${questionIndex}].is_submitting`, false);

        } catch (error) {
            toast.error("Failed to save question or choices.");
            formik.setFieldValue(`questions[${questionIndex}].is_submitting`, false);
        }
    };


    const handleDelete = async (questionIndex) => {
        const question = formik.values.questions[questionIndex];

        if (question.id) {
            try {
                // Start by setting deleting flag
                formik.setFieldValue(`questions[${questionIndex}].is_deleting`, true);

                // Step 1: Delete the question
                await dispatch(deleteQuestion({ question_id: question.id })).unwrap();
                toast.success("Question deleted successfully.");

                // Step 2: Delete all choices associated with the question
                for (let choiceIndex = 0; choiceIndex < question.choices.length; choiceIndex++) {
                    const choice = question.choices[choiceIndex];

                    if (choice.id) {
                        // Delete choice API call
                        await dispatch(deleteChoice({ choice_id: choice.id })).unwrap();
                        toast.success(`Choice ${choice.label} deleted successfully.`);
                    }
                }

                // After deleting, remove question from the form
                const updatedQuestions = [...formik.values.questions];
                updatedQuestions.splice(questionIndex, 1);
                formik.setFieldValue("questions", updatedQuestions);

            } catch (error) {
                toast.error("Failed to delete question or choices.");
                formik.setFieldValue(`questions[${questionIndex}].is_deleting`, false);
            }
        } else {
            toast.error("No question ID found, cannot delete.");
        }
    };


    return (
        <div>
            <div className="main-content">
                <div className="container-fluid bg-white form-box">
                    <h2 className="main-title">Assessment Questions</h2>
                    <div className="">
                        {formik.values.questions.map((question, questionIndex) => (
                            <div key={questionIndex} className="assessment-questions-box">
                                <Row>
                                    <Col lg={7}>
                                        <CustomInput type="textarea"
                                            name={`questions[${questionIndex}].text`}
                                            value={question.text}
                                            onChange={formik.handleChange}
                                            label={`Question ${questionIndex + 1}: `}
                                            placeholder="Enter question text" required />
                                    </Col>
                                </Row>
                                <h2 className="main-title label-title">Choices:</h2>
                                {question.choices.map((choice, choiceIndex) => (
                                    <div key={choiceIndex} className="questionery-box">
                                        <Row className="align-items-center mb-4 gap-3 gap-sm-0">
                                            <Col xl={8} lg={8} md={8} sm={12}>
                                                <div className="d-flex gap-2 align-items-center">
                                                    <Tooltip title="Delete choices">
                                                        <Button danger size="small" variant="default" loading={choice.is_deleting} onClick={() =>
                                                            handleDeleteChoice(questionIndex, choiceIndex)
                                                        } shape="circle" icon={<CloseOutlined />} />
                                                    </Tooltip>
                                                    <span className="text-uppercase">{choice.label}.</span>
                                                    <CustomInput
                                                        type="textarea"
                                                        name={`questions[${questionIndex}].choices[${choiceIndex}].text`}
                                                        value={choice.text}
                                                        onChange={formik.handleChange}
                                                        placeholder="Enter choice text"
                                                    />
                                                </div>
                                            </Col>
                                            <Col xl={4} lg={4} md={4} sm={12} className="mt-md-0 mt-sm-3">
                                                <div className="d-flex gap-2 custom-checkbox align-items-center">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        name={`questions[${questionIndex}].choices[${choiceIndex}].is_correct`}
                                                        checked={choice.is_correct}
                                                        onChange={formik.handleChange}
                                                    />
                                                    <p className="description-text m-0">
                                                        Correct Answer</p>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                ))}
                                <div>
                                    <div className="text-center">
                                        <Tooltip title="Add choices">
                                            <Button size="small" type="primary" onClick={() => handleAddChoice(questionIndex)} shape="circle" icon={<PlusOutlined />} />
                                        </Tooltip>
                                    </div>
                                    <div className="d-flex gap-3 mb-4  justify-content-end">
                                        <CustomButton
                                            minWidth="110px"
                                            name={question?.id ? "Update" : "Save"}
                                            disabled={question?.is_submitting}
                                            type="button"
                                            isLoading={question?.is_submitting}
                                            onClick={() => handleSave(questionIndex)}  // This triggers the save or update for both question and choices
                                        />
                                        <CustomButton
                                            minWidth="102px"
                                            type="button"
                                            disabled={question?.is_deleting}
                                            isLoading={question?.is_deleting}
                                            onClick={() => handleDelete(questionIndex)}  // This deletes both question and choices
                                            name="Delete"
                                            className='danger-btn'
                                        />
                                    </div>

                                </div>
                            </div>
                        ))}
                    </div>
                    <CustomButton type="button" onClick={handleAddQuestion} name="Add Question" />
                </div>
            </div>
        </div>
    );
};

export default CreateUpdateQuestions;
